<script setup lang="ts">
    import { getContrastTextColor } from '@chatbot/utils/color';

    // 2. Props
    const props = defineProps<{
        isOpen: boolean;
        mode: 'float' | 'fixed';
        avatarUrl?: string;
        title?: string;
        primaryColor: string;
        showTooltip?: boolean;
        tooltipText?: string;
        xPosition?: string;
        yPosition?: string;
    }>();

    // 3. Emits
    const emit = defineEmits<{
        toggle: [];
    }>();

    // 4. Composables
    const { t } = useI18n();

    // 5. Computed properties
    const buttonTextColor = computed<string>(() => getContrastTextColor(props.primaryColor));

    const buttonStyle = computed<Record<string, string>>(() => ({
        '--toggle-primary-color': props.primaryColor,
        '--toggle-button-text-color': buttonTextColor.value,
        '--toggle-tooltip-bg-color': props.primaryColor,
        '--toggle-tooltip-text-color': buttonTextColor.value,
        '--toggle-x-position': props.xPosition ?? '20px',
        '--toggle-y-position': props.yPosition ?? '20px',
    }));

    const ariaLabel = computed<string>(() => (props.isOpen ? t('chat.closeChat') : t('chat.openChat')));

    const shouldShowTooltip = computed<boolean>(() => Boolean(!props.isOpen && props.showTooltip && props.tooltipText));

    // 6. Methods / Functions
    const handleToggle = (): void => {
        emit('toggle');
    };
</script>

<template>
    <button
        v-if="mode === 'float'"
        type="button"
        class="chat-toggle-button"
        :class="{ 'show-tooltip': shouldShowTooltip }"
        :aria-label="ariaLabel"
        :style="buttonStyle"
        :data-tooltip="tooltipText"
        @click="handleToggle"
    >
        <span
            v-if="isOpen"
            class="toggle-icon"
        >
            <!-- Chevron down icon -->
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
            >
                <polyline points="6 9 12 15 18 9" />
            </svg>
        </span>
        <span
            v-else-if="avatarUrl"
            class="toggle-avatar"
        >
            <!-- Show avatar if set -->
            <img
                :src="avatarUrl"
                :alt="title || 'Chat'"
                class="avatar-image"
            >
        </span>
        <span
            v-else
            class="toggle-icon"
        >
            <!-- Chat bubble icon -->
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
            >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
        </span>
    </button>
</template>

<style lang="scss" scoped>
.chat-toggle-button {
    /* Toggle button always positioned at bottom right */
    position: fixed;
    z-index: 1001;
    display: flex;
    overflow: hidden;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: var(--toggle-primary-color);
    block-size: var(--size-12);
    box-shadow: 0 4px 12px rgb(0 0 0 / 0.15);
    color: rgb(255 255 255);
    cursor: pointer;
    inline-size: var(--size-12);
    inset-block-end: var(--toggle-y-position);
    inset-inline-end: var(--toggle-x-position);
    transition: all 0.3s ease;
}

.chat-toggle-button svg {
    color: var(--toggle-button-text-color);
}

/* Pseudo element tooltip styles */
.chat-toggle-button.show-tooltip::before {
    position: fixed;
    z-index: 1002;
    border-radius: var(--border-radius-xs) var(--border-radius-xs) 0;
    background: var(--toggle-tooltip-bg-color);
    box-shadow: 0 2px var(--spacing-02) rgb(0 0 0 / 0.1);
    color: var(--toggle-tooltip-text-color);
    content: attr(data-tooltip);
    font-size: var(--font-regular);
    font-weight: var(--font-weight-medium);
    inset-block-end: calc(var(--toggle-y-position));
    inset-inline-end: calc(var(--toggle-x-position));
    line-height: 1.3;
    max-inline-size: 300px;
    padding-block: var(--spacing-02);
    padding-inline: var(--spacing-3x);
    pointer-events: none;
    text-align: start;
    transform: translateY(-50px) translateX(-40px);
    white-space: pre-wrap;
    word-break: break-word;
}

.toggle-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--spacing-04);
}

.toggle-avatar {
    display: flex;
    overflow: hidden;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    block-size: auto;
    inline-size: 100%;
}

.avatar-image {
    aspect-ratio: 1/1;
    block-size: auto;
    inline-size: 100%;
    object-fit: cover;
}

/* Mobile Responsive Styles */
@media (width <= 480px) {
    .chat-toggle-button {
        block-size: var(--size-11);
        inline-size: var(--size-11);
        inset-block-end: var(--spacing-04);
        inset-inline-end: var(--spacing-04);
    }
}

@media (width <= 768px) {
    .chat-toggle-button {
        inset-block-end: var(--spacing-05);
        inset-inline-end: var(--spacing-05);
    }
}
</style>
